/**
 * CacheService - кеширование данных с версионированием
 *
 * Стратегия:
 * 1. При запуске Shell проверяем версии через HEAD /api/versions
 * 2. Сравниваем с localStorage
 * 3. Если версия изменилась — загружаем новые данные
 * 4. Возвращаем данные из кеша, если версия актуальна
 */

import type { CachedData } from '@/shell/types'

/**
 * Опции кеширования
 */
export interface CacheOptions {
  /** Время жизни кеша в миллисекундах (0 = бессрочно) */
  ttl?: number
  /** Использовать sessionStorage вместо localStorage */
  session?: boolean
}

/**
 * Результат проверки версии
 */
export interface VersionCheckResult {
  isStale: boolean
  cachedVersion?: string
  currentVersion: string
}

/**
 * Сервис кеширования с версионированием
 */
export class CacheService {
  private storage: Storage
  private memoryCache = new Map<string, CachedData<unknown>>()

  constructor(options: { session?: boolean } = {}) {
    this.storage = options.session ? sessionStorage : localStorage
  }

  /**
   * Получить данные с проверкой версии
   * Если версия изменилась — вызывает fetcher для получения новых данных
   */
  async getWithVersion<T>(
    key: string,
    currentVersion: string,
    fetcher: () => Promise<T>
  ): Promise<T> {
    // Сначала проверяем memory cache (самый быстрый)
    const memoryCached = this.memoryCache.get(key) as CachedData<T> | undefined
    if (memoryCached && memoryCached.version === currentVersion) {
      return memoryCached.data
    }

    // Затем проверяем localStorage
    const storageCached = this.get<CachedData<T>>(key)
    if (storageCached && storageCached.version === currentVersion) {
      // Обновляем memory cache
      this.memoryCache.set(key, storageCached)
      return storageCached.data
    }

    // Версия изменилась или кеша нет — загружаем новые данные
    const data = await fetcher()
    const cached: CachedData<T> = {
      version: currentVersion,
      data,
      cachedAt: new Date().toISOString(),
    }

    // Сохраняем в оба кеша
    this.set(key, cached)
    this.memoryCache.set(key, cached)

    return data
  }

  /**
   * Получить данные из кеша без проверки версии
   */
  getCached<T>(key: string): T | null {
    // Сначала memory cache
    const memoryCached = this.memoryCache.get(key) as CachedData<T> | undefined
    if (memoryCached) {
      return memoryCached.data
    }

    // Затем storage
    const storageCached = this.get<CachedData<T>>(key)
    if (storageCached) {
      this.memoryCache.set(key, storageCached)
      return storageCached.data
    }

    return null
  }

  /**
   * Получить версию кешированных данных
   */
  getCachedVersion(key: string): string | null {
    const memoryCached = this.memoryCache.get(key)
    if (memoryCached) {
      return memoryCached.version
    }

    const storageCached = this.get<CachedData<unknown>>(key)
    return storageCached?.version ?? null
  }

  /**
   * Проверить актуальность версии
   */
  checkVersion(key: string, currentVersion: string): VersionCheckResult {
    const cachedVersion = this.getCachedVersion(key)

    return {
      isStale: cachedVersion !== currentVersion,
      cachedVersion: cachedVersion ?? undefined,
      currentVersion,
    }
  }

  /**
   * Сохранить данные с версией
   */
  setWithVersion<T>(key: string, data: T, version: string): void {
    const cached: CachedData<T> = {
      version,
      data,
      cachedAt: new Date().toISOString(),
    }

    this.set(key, cached)
    this.memoryCache.set(key, cached)
  }

  /**
   * Инвалидировать кеш по ключу
   */
  invalidate(key: string): void {
    this.memoryCache.delete(key)
    this.storage.removeItem(key)
  }

  /**
   * Инвалидировать все ключи по префиксу
   */
  invalidateByPrefix(prefix: string): void {
    // Memory cache
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(prefix)) {
        this.memoryCache.delete(key)
      }
    }

    // Storage
    const keysToRemove: string[] = []
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key?.startsWith(prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => this.storage.removeItem(key))
  }

  /**
   * Очистить весь кеш TLH
   */
  clearAll(): void {
    this.memoryCache.clear()
    this.invalidateByPrefix('tlh:')
  }

  /**
   * Получить размер кеша в байтах (примерно)
   */
  getCacheSize(): { memory: number; storage: number } {
    let memorySize = 0
    for (const [key, value] of this.memoryCache.entries()) {
      memorySize += key.length + JSON.stringify(value).length
    }

    let storageSize = 0
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key?.startsWith('tlh:')) {
        const value = this.storage.getItem(key)
        storageSize += key.length + (value?.length ?? 0)
      }
    }

    return { memory: memorySize, storage: storageSize }
  }

  /**
   * Получить все ключи кеша
   */
  getKeys(): string[] {
    const keys = new Set<string>()

    // Memory cache keys
    for (const key of this.memoryCache.keys()) {
      keys.add(key)
    }

    // Storage keys
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key?.startsWith('tlh:')) {
        keys.add(key)
      }
    }

    return Array.from(keys)
  }

  // ============================================================================
  // Private helpers
  // ============================================================================

  private get<T>(key: string): T | null {
    try {
      const raw = this.storage.getItem(key)
      if (!raw) return null

      const parsed = JSON.parse(raw) as T
      return parsed
    } catch {
      // Если не удалось распарсить — удаляем невалидные данные
      this.storage.removeItem(key)
      return null
    }
  }

  private set<T>(key: string, value: T): void {
    try {
      this.storage.setItem(key, JSON.stringify(value))
    } catch (e) {
      // Если localStorage переполнен — очищаем старые данные
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        this.cleanupOldestEntries()
        // Пробуем ещё раз
        try {
          this.storage.setItem(key, JSON.stringify(value))
        } catch {
          console.warn(`[CacheService] Failed to cache ${key} - storage full`)
        }
      }
    }
  }

  /**
   * Очистить самые старые записи при переполнении
   */
  private cleanupOldestEntries(): void {
    const entries: { key: string; cachedAt: string }[] = []

    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i)
      if (key?.startsWith('tlh:')) {
        try {
          const raw = this.storage.getItem(key)
          if (raw) {
            const parsed = JSON.parse(raw) as CachedData<unknown>
            entries.push({ key, cachedAt: parsed.cachedAt })
          }
        } catch {
          // Удаляем невалидные записи
          this.storage.removeItem(key)
        }
      }
    }

    // Сортируем по дате (старые первыми)
    entries.sort((a, b) => a.cachedAt.localeCompare(b.cachedAt))

    // Удаляем 25% самых старых записей
    const toRemove = Math.max(1, Math.floor(entries.length * 0.25))
    for (let i = 0; i < toRemove; i++) {
      this.storage.removeItem(entries[i].key)
      this.memoryCache.delete(entries[i].key)
    }
  }
}

// ============================================================================
// Singleton instance
// ============================================================================

let cacheInstance: CacheService | null = null

/**
 * Получить singleton экземпляр CacheService
 */
export function getCacheService(): CacheService {
  if (!cacheInstance) {
    cacheInstance = new CacheService()
  }
  return cacheInstance
}

/**
 * Создать новый экземпляр CacheService (для тестирования)
 */
export function createCacheService(options?: { session?: boolean }): CacheService {
  return new CacheService(options)
}

export default CacheService
